import type { ProductImageProviderProps } from './types';
import type { FC, PropsWithChildren } from 'react';

import { useMemo } from 'react';

import { FLAT_IMAGE_TYPES, IMAGE_TYPE } from './constants';
import { TileImageProvider } from './TileImageContext';
import { buildImageSource, hasSpecialImage, resolveImageProfiles } from './utilities';
import type {
  HoverImageType,
  PrimaryImageType,
} from '~/amplienceTypes/schemas/imported/product-recommendations-schema';
import { useTileContext } from '~/components/shared/recommendations/tile/TileContext';
import type { SimplifiedImage } from '~/types/algolia';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { localisedData } from '~/utilities/helpers';
import { findProfileImage } from '~/utilities/helpers/findImageProfile';

export const GStarProductImageProvider: FC<PropsWithChildren<ProductImageProviderProps>> = ({
  children,
  product,
}) => {
  const {
    configuration: {
      allowedProductTilePrimaryImageTypes,
      allowedProductTileHoverImageTypes,
      allowedProductTileSpecialMaterialHoverImageTypes,
    },
  } = useStaticContext();
  const { baseImageUrl, aspectRatio, primaryImageType, hoverImageType } = useTileContext();

  const value = useMemo(() => {
    const findImageByProfiles = findProfileImage(
      new Map(Object.entries(product?.simplifiedImageJson ?? {}))
    );

    const resolveProfiles = (imageType: PrimaryImageType | HoverImageType) =>
      resolveImageProfiles({
        imageType,
        primaryProfiles: allowedProductTilePrimaryImageTypes,
        hoverProfiles: allowedProductTileHoverImageTypes,
        flatProfiles: FLAT_IMAGE_TYPES,
      });

    const mainProfiles = resolveProfiles(primaryImageType ?? IMAGE_TYPE.defaultPrimary);
    const hoverProfiles = resolveProfiles(hoverImageType ?? IMAGE_TYPE.defaultHover);
    // When both views resolve to the same image, there is nothing to swap to on hover
    const hoverDisabled = hoverProfiles === mainProfiles;

    const getHoverImage = () => {
      if (hoverDisabled) {
        return undefined;
      }

      const materialGroup = localisedData(product, product.materialgroup, 'EN') ?? '';
      const hasSpecialImageFlag = hasSpecialImage(materialGroup);

      const baseHoverImage = findImageByProfiles(hoverProfiles);
      const specialMaterialImage = findImageByProfiles(
        allowedProductTileSpecialMaterialHoverImageTypes
      );

      let hoverImage = baseHoverImage;

      // special-material hover only applies to the default hover view
      if (
        hoverProfiles === allowedProductTileHoverImageTypes &&
        hasSpecialImageFlag &&
        specialMaterialImage?.url
      ) {
        hoverImage = specialMaterialImage;
      }

      // style variant hover images
      if (!hoverImage?.url && product.hoverImage) {
        hoverImage = product.hoverImage as SimplifiedImage;
      }

      return hoverImage;
    };

    const getPrimaryImage = () => {
      let primaryImage = findImageByProfiles(mainProfiles);

      // style variant primary image
      if (!primaryImage?.url && product.primaryImage) {
        primaryImage = product.primaryImage as SimplifiedImage;
      }

      return primaryImage;
    };

    const primaryImage = getPrimaryImage();
    const hoverImage = getHoverImage();

    return {
      primaryImage: buildImageSource(
        primaryImage?.url,
        baseImageUrl,
        aspectRatio,
        primaryImage?.isAI
      ),
      hoverImage: buildImageSource(hoverImage?.url, baseImageUrl, aspectRatio, hoverImage?.isAI),
    };
  }, [
    allowedProductTileHoverImageTypes,
    allowedProductTilePrimaryImageTypes,
    allowedProductTileSpecialMaterialHoverImageTypes,
    baseImageUrl,
    aspectRatio,
    product,
    primaryImageType,
    hoverImageType,
  ]);

  return <TileImageProvider value={value}>{children}</TileImageProvider>;
};
